import { ButtonRadioField } from '@/components/form/fields/radio/ButtonRadioInput';
import { Loading } from '@/components/loading/Loading';
import SiEyeOff from '@/icons/SiEyeOff';
import SiTrendingUpFill from '@/icons/SiTrendingUpFill';
import {
  FilterType,
  UpdateFilterPackArgs,
  useCategories,
  useCreateFilter,
  useFilterPack,
  useManufacturers,
  useUpdateFilterPack
} from '@/services/api/hooks/catalog';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DecimalField } from '@/components/form/fields/text/DecimalInput';
import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import LocalizedStrings from 'react-localization';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import { CriteriaField } from './CriteriaField';

const strings = new LocalizedStrings({
  en: {
    createFilterModalTitle: 'Apply a catalogue filter',
    createFilterModalDescription:
      'Hide specific manufacturers from your customers and set specific global margins by manufacturer or category.',
    createFilterMarginWarning: 'Please note the maximum margin that can be applied for ‘True-DaaS’ is 25%',
    createFilterModalTypeLabel: 'Type',
    createFilterModalTypeDescription:
      'The effect you want this filter to have on the affected products. You can only choose one of these per filter.',
    createFilterModalVisibilityOptionLabel: 'Visibility',
    createFilterModalMarginOptionLabel: 'Margin',
    createFilterModalCriteriaLabel: 'Criteria',
    createFilterModalCriteriaDescription:
      'The criteria that will be used to determine which products are affected by this filter.',
    createFilterModalCriteriaPlaceholder: 'Select one',
    createFilterModalCategoryCriteriaLabel: 'Category',
    createFilterModalManufacturerCriteriaLabel: 'Manufacturer',
    createFilterModalValueLabel: 'Value',
    createFilterModalValuePlaceholder: 'abc',
    createFilterModalValueSuffix: '%',
    createFilterModalCreateButtonLabel: 'Create'
  }
});

const CreateFilterFormFields = z.object({
  type: z.nativeEnum(FilterType),
  value: z.string().optional(),
  criteria: z.record(
    z.object({
      id: z.string(),
      name: z.string()
    })
  )
});

type CreateFilterFormFields = z.infer<typeof CreateFilterFormFields>;

export const CreateFilterModal: FC<ModalProps> = props => {
  const CreateFilterForm = () => {
    const filterPackId = useCatalogueService(state => state.filterPackId);
    if (!filterPackId) throw new Error('Missing filter pack id');
    const [filterPack] = useFilterPack(filterPackId);
    const createFilter = useCreateFilter();
    const updateFilterPack = useUpdateFilterPack();

    const methods = useForm<CreateFilterFormFields>({ resolver: zodResolver(CreateFilterFormFields) });
    const [manufacturers] = useManufacturers();
    const [categories] = useCategories();

    const onSubmit = async (data: CreateFilterFormFields) => {
      const filter = await createFilter.mutateAsync({
        type: data.type,
        criteria: data.criteria,
        value: data.value !== undefined ? Number(data.value) : undefined
      });

      const updatedFilterPack: UpdateFilterPackArgs = {
        id: filterPack.id,
        globalMarginModifier: filterPack.globalMarginModifier,
        marginFilterIds: filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: filterPack.productFilters.map(filter => filter.id)
      };

      if (data.type === FilterType.Margin) {
        updatedFilterPack.marginFilterIds.push(filter.id);
      }

      if (data.type === FilterType.Hidden) {
        updatedFilterPack.productFilterIds.push(filter.id);
      }

      await updateFilterPack.mutateAsync(updatedFilterPack);
      props.close();
    };

    const selectedType = methods.watch('type');
    const hasModifier = selectedType === FilterType.Margin;

    return (
      <Form className="flex w-full flex-col gap-4 py-2" methods={methods} onSubmit={onSubmit}>
        <ButtonRadioField
          id="type"
          label={strings.createFilterModalTypeLabel}
          description={strings.createFilterModalTypeDescription}
          options={[
            {
              id: FilterType.Hidden,
              label: (
                <>
                  <SiEyeOff className="text-xl" />
                  <span>{strings.createFilterModalVisibilityOptionLabel}</span>
                </>
              )
            },
            {
              id: FilterType.Margin,
              label: (
                <>
                  <SiTrendingUpFill className="text-xl" />
                  <span>{strings.createFilterModalMarginOptionLabel}</span>
                </>
              )
            }
          ]}
        />
        <CriteriaField
          id="criteria"
          label={strings.createFilterModalCriteriaLabel}
          description={strings.createFilterModalCriteriaDescription}
          placeholder={strings.createFilterModalCriteriaPlaceholder}
          criteria={[
            {
              options: categories.map(category => ({ id: category.id, name: category.name })),
              id: 'category',
              label: strings.createFilterModalCategoryCriteriaLabel
            },
            {
              options: manufacturers.map(manufacturer => ({ id: manufacturer.id, name: manufacturer.name })),
              id: 'manufacturer',
              label: strings.createFilterModalManufacturerCriteriaLabel
            }
          ]}
        />
        {hasModifier && (
          <DecimalField
            id="value"
            maxDp={10}
            label={strings.createFilterModalValueLabel}
            placeholder={strings.createFilterModalValuePlaceholder}
            suffix={strings.createFilterModalValueSuffix}
          />
        )}
        <Button
          loading={createFilter.isLoading || updateFilterPack.isLoading}
          className="w-fit place-self-end"
          variant={ButtonVariant.Primary}
          type="submit"
        >
          {strings.createFilterModalCreateButtonLabel}
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">{strings.createFilterModalTitle}</h1>
        </Modal.Controls>
        <p className="mt-0.5 leading-tight">{strings.createFilterModalDescription}</p>
        <Suspense fallback={<Loading />}>
          <CreateFilterForm />
        </Suspense>
        <p className="mt-0.5 font-semibold leading-tight">{strings.createFilterMarginWarning}</p>
      </Card>
    </Modal>
  );
};
